import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { useAuth } from '../context/AuthContext';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import StatusBadge from '../components/common/StatusBadge';
import { SkeletonCard, SkeletonTable } from '../components/common/Skeleton';
import CopyButton from '../components/common/CopyButton';
import { Wallet, ArrowDownRight, Copy, Check, Hash, ShieldAlert, Plus, History, DollarSign, Zap, Clock, MessageSquare } from 'lucide-react';

const TelegramIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
  </svg>
);

const WhatsappIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.05 4.91A9.816 9.816 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01zm-7.01 15.24c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.32a8.198 8.198 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c.02 4.54-3.68 8.24-8.22 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.8-.23-.08-.39-.12-.56.12-.17.25-.64.8-.79.97-.15.17-.3.19-.55.07-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.15-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.17-.25.25-.42.08-.17.04-.32-.02-.45-.06-.12-.56-1.35-.77-1.85-.2-.49-.4-.42-.55-.43-.14-.01-.3-.01-.46-.01s-.42.06-.64.3c-.22.24-.85.83-.85 2.03 0 1.2.87 2.36.99 2.52.12.17 1.72 2.62 4.16 3.67.58.25 1.03.4 1.38.51.58.18 1.11.16 1.53.1.47-.07 1.47-.6 1.67-1.18.2-.58.2-1.08.14-1.18-.06-.1-.22-.16-.47-.28z"/>
  </svg>
);

const WalletPage = () => {
  const { refreshUser } = useAuth();
  const [walletData, setWalletData] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState({ type: '', text: '' });

  // Activation Fee State
  const [activationInfo, setActivationInfo] = useState({
    isActivated: false,
    activationStatus: 'unpaid',
    activatedAt: null,
    activationFeeAmount: 100,
    activationFeeOption1: 100,
    activationFeeOption2: 60,
    activationAdminSplit: 80,
    activationReferrerSplit: 20,
    depositUSDTAddress: '',
    pendingPayment: null
  });
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [selectedActivationAmount, setSelectedActivationAmount] = useState(100);
  const [activationTxid, setActivationTxid] = useState('');
  const [activationNetwork, setActivationNetwork] = useState('USDT-BEP20');
  const [submittingActivation, setSubmittingActivation] = useState(false);

  // Form states
  const [amount, setAmount] = useState('');
  const [network, setNetwork] = useState('USDT-BEP20');
  const [txid, setTxid] = useState('');

  const fetchActivationStatus = async () => {
    try {
      const res = await axiosClient.get('/wallet/activation-status');
      setActivationInfo(res.data);
    } catch (err) {
      console.error('[Activation Status Error]:', err);
    }
  };

  const fetchWallet = async () => {
    try {
      const res = await axiosClient.get('/wallet/my-wallet');
      setWalletData(res.data.wallet || null);
      setDeposits(res.data.deposits || []);
      setTransactions(res.data.transactions || []);
      await fetchActivationStatus();
      if (refreshUser) refreshUser();
    } catch (err) {
      console.error('[Wallet Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallet();
  }, []);

  const handleCopyAddress = () => {
    const addr = walletData?.depositUSDTAddress || activationInfo.depositUSDTAddress || 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t';
    navigator.clipboard.writeText(addr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDepositSubmit = async (e) => {
    e.preventDefault();
    if (!amount || !txid) return;
    setSubmitting(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      await axiosClient.post('/wallet/deposit', {
        amount,
        network: activeDepositNetwork,
        txid
      });
      setFeedbackMsg({ type: 'success', text: 'USDT Deposit submitted successfully! Admin will verify blockchain TXID.' });
      setAmount('');
      setTxid('');
      setShowDepositModal(false);
      fetchWallet();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to submit deposit request.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivationSubmit = async (e) => {
    e.preventDefault();
    if (!activationTxid) return;
    setSubmittingActivation(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      const opt2 = activationInfo.activationFeeOption2 || 60;
      await axiosClient.post('/wallet/pay-activation-fee', {
        network: activeDepositNetwork,
        txid: activationTxid,
        amount: selectedActivationAmount,
        optionTier: selectedActivationAmount === opt2 ? 2 : 1
      });
      setFeedbackMsg({ type: 'success', text: `Activation fee deposit of $${selectedActivationAmount} USDT submitted successfully! Admin will verify blockchain TXID.` });
      setActivationTxid('');
      setShowActivationModal(false);
      fetchWallet();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to submit activation fee deposit.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setSubmittingActivation(false);
    }
  };

  const opt1 = activationInfo.activationFeeOption1 || 100;
  const opt2 = activationInfo.activationFeeOption2 || 60;

  const activeDepositNetwork = walletData?.depositNetwork || activationInfo?.depositNetwork || 'USDT (BEP20 - BNB Smart Chain)';

  const getNetworkBadge = (net) => {
    if (!net) return 'BEP20';
    if (net.includes('BEP20')) return 'BEP20';
    if (net.includes('TRC20')) return 'TRC20';
    if (net.includes('ERC20')) return 'ERC20';
    return 'USDT';
  };

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-extrabold text-[#FFFFFF]">Internal Wallet & USDT Deposits</h2>
            {activationInfo.isActivated && (
              <span className="px-2 py-0.5 rounded text-[10px] font-extrabold bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/40">
                ✓ ACTIVATED
              </span>
            )}
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Manage your wallet balance, submit USDT deposits with transaction hash (TXID), and view immutable financial transaction logs.
          </p>
        </div>

        {/* Buttons Section: Pay Activation Fee Options on the LEFT side of Deposit USDT */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {activationInfo.isActivated ? (
            <div className="flex items-center space-x-1.5 px-3.5 py-2 rounded-xl bg-[#10B981]/20 border border-[#10B981]/40 text-[#10B981] text-xs font-extrabold">
              <Check className="w-4 h-4" />
              <span>Account Activated</span>
            </div>
          ) : activationInfo.activationStatus === 'pending' || activationInfo.pendingPayment ? (
            <div className="flex items-center space-x-1.5 px-3.5 py-2 rounded-xl bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[#F59E0B] text-xs font-extrabold">
              <Clock className="w-4 h-4 animate-pulse" />
              <span>Activation Under Review (${activationInfo.pendingPayment?.amount || opt1})</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Button 
                variant="warning" 
                onClick={() => {
                  setSelectedActivationAmount(opt1);
                  setShowActivationModal(true);
                }}
                className="flex items-center space-x-1.5 bg-[#F97316] hover:bg-[#EA580C] text-white border-0 text-xs font-bold"
              >
                <Zap className="w-4 h-4 text-white fill-white" />
                <span>Pay ${opt1} USDT</span>
              </Button>

              <Button 
                variant="warning" 
                onClick={() => {
                  setSelectedActivationAmount(opt2);
                  setShowActivationModal(true);
                }}
                className="flex items-center space-x-1.5 bg-[#F59E0B] hover:bg-[#D97706] text-black border-0 text-xs font-bold"
              >
                <Zap className="w-4 h-4 text-black fill-black" />
                <span>Pay ${opt2} USDT</span>
              </Button>
            </div>
          )}

          <Button 
            variant="primary" 
            onClick={() => {
              if (!activationInfo.isActivated) {
                setFeedbackMsg({
                  type: 'error',
                  text: `Please pay either the $${opt1} or $${opt2} USDT Activation Fee first and await admin approval before making standard USDT deposits.`
                });
                return;
              }
              setShowDepositModal(true);
            }}
            disabled={!activationInfo.isActivated}
            className={`flex items-center space-x-2 ${
              !activationInfo.isActivated ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={!activationInfo.isActivated ? "Pay activation fee first to unlock USDT deposits" : "Deposit USDT"}
          >
            <Plus className="w-4 h-4" />
            <span>Deposit USDT</span>
          </Button>
        </div>
      </div>

      {!activationInfo.isActivated && (
        (activationInfo.activationStatus === 'pending' || activationInfo.pendingPayment) ? (
          /* Pending Under Review Banner */
          <div className="p-4 rounded-2xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-[#F59E0B]">
            <div className="flex items-center space-x-2.5">
              <Zap className="w-5 h-5 text-[#F59E0B] shrink-0 fill-current" />
              <div>
                <p className="font-bold text-[#FFFFFF]">Activation Payment Submitted & Under Review</p>
                <p className="text-[11px] text-[#9CA3AF] mt-0.5">
                  Your ${activationInfo.pendingPayment?.amount || opt1} USDT activation payment is submitted and awaiting admin TXID verification.
                </p>
              </div>
            </div>
          </div>
        ) : activationInfo.rejectedPayment ? (
          /* Prominent Rejection Banner (Only when NOT pending) */
          <div className="p-4 rounded-2xl bg-[#EF4444]/15 border border-[#EF4444]/40 text-xs text-[#EF4444] space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start space-x-3">
                <ShieldAlert className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-extrabold text-white text-sm">Activation Fee Deposit Rejected by Admin</p>
                  <p className="text-[#9CA3AF]">
                    Reason: <span className="font-bold text-[#EF4444]">{activationInfo.rejectedPayment.adminNotes || 'TXID verification failed or payment invalid.'}</span>
                  </p>
                  <p className="text-[11px] text-[#9CA3AF] font-mono">
                    Submitted TXID: <span className="text-[#F59E0B]">{activationInfo.rejectedPayment.txid}</span>
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2 shrink-0">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setSelectedActivationAmount(opt1);
                    setActivationTxid('');
                    setShowActivationModal(true);
                  }}
                  className="font-bold bg-[#EF4444] hover:bg-[#DC2626] text-white border-0 text-xs"
                >
                  Pay ${opt1}
                </Button>

                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setSelectedActivationAmount(opt2);
                    setActivationTxid('');
                    setShowActivationModal(true);
                  }}
                  className="font-bold bg-[#F59E0B] hover:bg-[#D97706] text-black border-0 text-xs"
                >
                  Pay ${opt2}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* Standard Unpaid Banner */
          <div className="p-4 rounded-2xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-[#F59E0B]">
            <div className="flex items-center space-x-2.5">
              <Zap className="w-5 h-5 text-[#F59E0B] shrink-0 fill-current" />
              <div>
                <p className="font-bold text-[#FFFFFF]">Account Activation Required</p>
                <p className="text-[11px] text-[#9CA3AF] mt-0.5">
                  Select either the <span className="text-[#F97316] font-bold">${opt1} USDT</span> or <span className="text-[#F59E0B] font-bold">${opt2} USDT</span> activation option to activate your account and unlock standard USDT deposits.
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 shrink-0">
              <Button
                variant="warning"
                size="sm"
                onClick={() => {
                  setSelectedActivationAmount(opt1);
                  setShowActivationModal(true);
                }}
                className="bg-[#F97316] text-white font-bold hover:bg-[#EA580C]"
              >
                Pay ${opt1}
              </Button>

              <Button
                variant="warning"
                size="sm"
                onClick={() => {
                  setSelectedActivationAmount(opt2);
                  setShowActivationModal(true);
                }}
                className="bg-[#F59E0B] text-black font-bold hover:bg-[#D97706]"
              >
                Pay ${opt2}
              </Button>
            </div>
          </div>
        )
      )}

      {feedbackMsg.text && (
        <div className={`p-4 rounded-2xl border text-xs font-semibold ${
          feedbackMsg.type === 'success' 
            ? 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]' 
            : 'bg-[#EF4444]/15 border-[#EF4444]/30 text-[#EF4444]'
        }`}>
          {feedbackMsg.text}
        </div>
      )}

      {/* Wallet Overview Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <Card hoverEffect>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#9CA3AF] font-semibold">Current Balance</span>
                <Wallet className="w-4 h-4 text-[#F59E0B] shrink-0" />
              </div>
              <p className="text-2xl font-extrabold text-[#FFFFFF] mt-2">${walletData?.balance ? walletData.balance.toFixed(2) : '0.00'}</p>
              <div className="mt-2 flex items-center space-x-1">
                <StatusBadge status="approved" label="Available USDT" />
              </div>
            </Card>

            <Card hoverEffect>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#9CA3AF] font-semibold">Total Deposited</span>
                <ArrowDownRight className="w-4 h-4 text-[#10B981] shrink-0" />
              </div>
              <p className="text-2xl font-extrabold text-[#10B981] mt-2">${walletData?.totalDeposited ? walletData.totalDeposited.toFixed(2) : '0.00'}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1">Verified USDT deposits</p>
            </Card>

            <Card hoverEffect>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#9CA3AF] font-semibold">Profit Share Deducted</span>
                <DollarSign className="w-4 h-4 text-[#F97316] shrink-0" />
              </div>
              <p className="text-2xl font-extrabold text-[#F97316] mt-2">${walletData?.totalProfitDeducted ? walletData.totalProfitDeducted.toFixed(2) : '0.00'}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1">Total profit sharing paid</p>
            </Card>

            <Card hoverEffect>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#9CA3AF] font-semibold">Referral Commission</span>
                <History className="w-4 h-4 text-[#3B82F6] shrink-0" />
              </div>
              <p className="text-2xl font-extrabold text-[#3B82F6] mt-2">${walletData?.totalReferralEarned ? walletData.totalReferralEarned.toFixed(2) : '0.00'}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1">Referral commission earnings</p>
            </Card>
          </>
        )}
      </div>

      {/* 24/7 Official Support & Query Help Banner */}
      <div className="bg-[#121215] p-5 rounded-2xl border border-[#3B82F6]/30 bg-gradient-to-r from-[#0F172A] via-[#121215] to-[#1E1B4B]/50 shadow-card flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-start space-x-3.5">
          <div className="p-2.5 rounded-2xl bg-[#3B82F6]/10 border border-[#3B82F6]/30 shrink-0 mt-0.5">
            <MessageSquare className="w-5 h-5 text-[#60A5FA]" />
          </div>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-extrabold text-[#FFFFFF]">Need Help or Encountered a Failed Transaction?</h3>
              <span className="px-2 py-0.5 rounded-full text-[9px] font-extrabold bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30 uppercase tracking-wider">24/7 Live Support</span>
            </div>
            <p className="text-xs text-[#9CA3AF] max-w-2xl">
              Have any questions about USDT deposits, account activation, or transaction verification? Contact our official support team directly via Telegram or WhatsApp for immediate assistance.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto shrink-0">
          <a
            href={activationInfo.telegramSupportLink || 'https://t.me/dubaigoldx_support'}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 sm:flex-initial flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl bg-[#0088cc]/15 hover:bg-[#0088cc]/25 border border-[#0088cc]/40 text-[#38BDF8] text-xs font-extrabold transition-all hover:scale-[1.02] shadow-[0_2px_12px_rgba(0,136,204,0.2)]"
          >
            <TelegramIcon className="w-4 h-4 text-[#38BDF8]" />
            <span>Telegram Support</span>
          </a>

          <a
            href={activationInfo.whatsappSupportLink || 'https://wa.me/1234567890'}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 sm:flex-initial flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/40 text-[#22C55E] text-xs font-extrabold transition-all hover:scale-[1.02] shadow-[0_2px_12px_rgba(37,211,102,0.2)]"
          >
            <WhatsappIcon className="w-4 h-4 text-[#22C55E]" />
            <span>WhatsApp Support</span>
          </a>
        </div>
      </div>

      {/* Your USDT Deposit Requests Table */}
      <Card title="Your USDT Deposit Requests" subtitle="Status of submitted blockchain deposit requests">
        {loading ? (
          <SkeletonTable rows={3} cols={5} />
        ) : deposits.length === 0 ? (
          <div className="p-8 text-center text-xs text-[#9CA3AF]">
            No deposit requests submitted yet. Click <span className="text-[#F97316] font-bold">Deposit USDT</span> to submit your first deposit.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Amount ($)</th>
                  <th className="py-3 px-4">Network</th>
                  <th className="py-3 px-4">Blockchain TXID</th>
                  <th className="py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {deposits.map((dep) => (
                  <tr key={dep._id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {new Date(dep.createdAt).toLocaleString()}
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#10B981]">
                      +${dep.amount.toFixed(2)} USD
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[#9CA3AF]">
                      {dep.network}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[#F59E0B]">
                      <div className="flex items-center space-x-1">
                        <span>{dep.txid ? `${dep.txid.substring(0, 14)}...` : 'N/A'}</span>
                        {dep.txid && <CopyButton text={dep.txid} />}
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <StatusBadge status={dep.status} label={dep.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Immutable Financial Audit Ledger Table */}
      <Card title="Immutable Financial Audit Ledger" subtitle="Complete record of deposits, profit deductions, and referral earnings">
        {loading ? (
          <SkeletonTable rows={4} cols={6} />
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-xs text-[#9CA3AF]">
            No wallet transactions recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">Timestamp</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Amount ($)</th>
                  <th className="py-3 px-4">Bal Before</th>
                  <th className="py-3 px-4">Bal After</th>
                  <th className="py-3 px-4">Reference / Notes</th>
                  <th className="py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {transactions.map((tx) => {
                  const isCredit = ['deposit', 'referral_commission', 'activation_referral_commission'].includes(tx.type);
                  return (
                    <tr key={tx._id} className="hover:bg-[#1A1A1E] transition-colors">
                      <td className="py-3.5 px-4 text-[#9CA3AF]">
                        {new Date(tx.createdAt).toLocaleString()}
                      </td>
                      <td className="py-3.5 px-4 uppercase font-bold text-[10px]">
                        <span className={`px-2 py-0.5 rounded ${
                          isCredit ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#F97316]/20 text-[#F97316]'
                        }`}>
                          {tx.type?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className={`py-3.5 px-4 font-mono font-bold ${
                        isCredit ? 'text-[#10B981]' : 'text-[#F97316]'
                      }`}>
                        {isCredit ? '+' : '-'}${tx.amount.toFixed(2)} USD
                      </td>
                      <td className="py-3.5 px-4 font-mono text-[#9CA3AF]">
                        ${(tx.balanceBefore || 0).toFixed(2)}
                      </td>
                      <td className="py-3.5 px-4 font-mono font-bold text-[#FFFFFF]">
                        ${(tx.balanceAfter || 0).toFixed(2)}
                      </td>
                      <td className="py-3.5 px-4 text-[#9CA3AF] max-w-xs truncate">
                        {tx.notes || 'N/A'}
                      </td>
                      <td className="py-3.5 px-4">
                        <StatusBadge status={tx.status || 'completed'} label={tx.status || 'completed'} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Activation Fee Deposit Modal */}
      {showActivationModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <div>
                <h3 className="text-base font-extrabold text-[#FFFFFF]">Submit Platform Activation Fee</h3>
                <p className="text-xs text-[#F97316] font-bold mt-0.5">
                  Selected Tier: ${selectedActivationAmount} USDT
                </p>
              </div>
              <button onClick={() => setShowActivationModal(false)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            {/* Select Activation Tier Option inside modal */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSelectedActivationAmount(opt1)}
                className={`p-3 rounded-2xl border text-center transition-all ${
                  selectedActivationAmount === opt1 
                    ? 'bg-[#F97316]/20 border-[#F97316] text-white font-bold' 
                    : 'bg-[#09090B] border-[#27272A] text-[#9CA3AF] hover:text-white'
                }`}
              >
                <div className="text-xs font-extrabold">Option 1</div>
                <div className="text-base font-extrabold text-[#F97316] mt-0.5">${opt1} USDT</div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedActivationAmount(opt2)}
                className={`p-3 rounded-2xl border text-center transition-all ${
                  selectedActivationAmount === opt2 
                    ? 'bg-[#F59E0B]/20 border-[#F59E0B] text-white font-bold' 
                    : 'bg-[#09090B] border-[#27272A] text-[#9CA3AF] hover:text-white'
                }`}
              >
                <div className="text-xs font-extrabold">Option 2</div>
                <div className="text-base font-extrabold text-[#F59E0B] mt-0.5">${opt2} USDT</div>
              </button>
            </div>

            {/* Official Deposit Address Widget */}
            <div className="p-4 bg-[#09090B] rounded-2xl border border-[#27272A] space-y-2">
              <span className="text-[10px] uppercase font-bold text-[#9CA3AF]">Official Deposit Address ({getNetworkBadge(activeDepositNetwork)})</span>
              <div className="flex items-center justify-between bg-[#121215] p-2.5 rounded-xl border border-[#27272A]">
                <span className="font-mono text-xs text-[#F59E0B] truncate max-w-[240px]">
                  {walletData?.depositUSDTAddress || activationInfo.depositUSDTAddress || 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'}
                </span>
                <button
                  onClick={handleCopyAddress}
                  className="p-1.5 rounded-lg bg-[#27272A] hover:bg-[#F97316] text-[#FFFFFF] transition-colors shrink-0"
                  title="Copy Deposit Address"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              {copied && <p className="text-[10px] text-[#10B981] font-semibold">✓ Address copied to clipboard!</p>}
            </div>

            <form onSubmit={handleActivationSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Network</label>
                <div className="w-full bg-[#09090B] border border-[#27272A] rounded-xl px-3.5 py-3 text-xs flex items-center justify-between">
                  <span className="text-[#FFFFFF] font-medium">{activeDepositNetwork}</span>
                  <span className="text-[10px] font-mono font-bold bg-[#F59E0B]/15 text-[#F59E0B] px-2 py-0.5 rounded border border-[#F59E0B]/30">
                    {getNetworkBadge(activeDepositNetwork)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Blockchain Transaction Hash (TXID)</label>
                <div className="relative">
                  <Hash className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                  <input
                    type="text"
                    required
                    value={activationTxid}
                    onChange={(e) => setActivationTxid(e.target.value)}
                    placeholder={`Paste TXID hash for $${selectedActivationAmount} payment...`}
                    className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs font-mono focus:outline-none focus:border-[#F97316]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-[#27272A]">
                <Button variant="secondary" size="sm" type="button" onClick={() => setShowActivationModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" disabled={submittingActivation}>
                  {submittingActivation ? 'Submitting...' : `Submit $${selectedActivationAmount} Activation Fee`}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <h3 className="text-base font-extrabold text-[#FFFFFF]">Submit USDT Deposit</h3>
              <button onClick={() => setShowDepositModal(false)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            {/* Official Deposit Address Widget */}
            <div className="p-4 bg-[#09090B] rounded-2xl border border-[#27272A] space-y-2">
              <span className="text-[10px] uppercase font-bold text-[#9CA3AF]">Official Deposit Address ({getNetworkBadge(activeDepositNetwork)})</span>
              <div className="flex items-center justify-between bg-[#121215] p-2.5 rounded-xl border border-[#27272A]">
                <span className="font-mono text-xs text-[#F59E0B] truncate max-w-[240px]">
                  {walletData?.depositUSDTAddress || activationInfo.depositUSDTAddress || 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'}
                </span>
                <button
                  onClick={handleCopyAddress}
                  className="p-1.5 rounded-lg bg-[#27272A] hover:bg-[#F97316] text-[#FFFFFF] transition-colors shrink-0"
                  title="Copy Deposit Address"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              {copied && <p className="text-[10px] text-[#10B981] font-semibold">✓ Address copied to clipboard!</p>}
            </div>

            <form onSubmit={handleDepositSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Network</label>
                <div className="w-full bg-[#09090B] border border-[#27272A] rounded-xl px-3.5 py-3 text-xs flex items-center justify-between">
                  <span className="text-[#FFFFFF] font-medium">{activeDepositNetwork}</span>
                  <span className="text-[10px] font-mono font-bold bg-[#F59E0B]/15 text-[#F59E0B] px-2 py-0.5 rounded border border-[#F59E0B]/30">
                    {getNetworkBadge(activeDepositNetwork)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Deposit Amount ($ USD)</label>
                <div className="relative">
                  <DollarSign className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                  <input
                    type="number"
                    min="1"
                    required
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="e.g. 500"
                    className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs font-bold focus:outline-none focus:border-[#F97316]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Blockchain Transaction Hash (TXID)</label>
                <div className="relative">
                  <Hash className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                  <input
                    type="text"
                    required
                    value={txid}
                    onChange={(e) => setTxid(e.target.value)}
                    placeholder="Paste transaction TXID hash..."
                    className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs font-mono focus:outline-none focus:border-[#F97316]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-[#27272A]">
                <Button variant="secondary" size="sm" type="button" onClick={() => setShowDepositModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Deposit'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletPage;
